// client/src/coms/views/chatview_parts/useChatModerationActions.js
import { safeObj, toInt, apiFetchJson } from './chatUtils';

export default function useChatModerationActions({
  myUserId,
  ownerUserId,
  myPermissionsMap,
  setEditingMessageId,
  setEditDraft,
  setEditBusyMessageId,
  setDeleteBusyMessageId,
  setPinBusyMessageId,
  setSuperPinBusyMessageId,
  setMessageActionErr,
  setHoveredMessageId,
  editDraft,
  applyIncomingUpdateLike,
  removeMessageById,
  onMessageMetaChanged,
  onMessageRemoved,
}) {
  function hasPermission(key) {
    const map = myPermissionsMap && typeof myPermissionsMap === 'object' ? myPermissionsMap : {};
    return toInt(map[key], 0) === 1;
  }

  function getMessageId(msg) {
    const m = safeObj(msg);
    return m.id != null ? String(m.id).trim() : '';
  }

  function isMyMessage(msg) {
    const m = safeObj(msg);
    const uid = m.user_id != null ? toInt(m.user_id, null) : null;
    const meId = myUserId != null ? toInt(myUserId, null) : null;
    if (!meId || uid == null) return false;
    return meId === uid;
  }

  function isServerOwner() {
    const meId = myUserId != null ? toInt(myUserId, null) : null;
    const ownerId = ownerUserId != null ? toInt(ownerUserId, null) : null;
    if (!meId || !ownerId) return false;
    return meId === ownerId;
  }

  function isSystemAdmin() {
    const meId = myUserId != null ? toInt(myUserId, null) : null;
    return meId === 1;
  }

  function canPinMessage(msg) {
    if (!getMessageId(msg)) return false;
    if (isSystemAdmin()) return true;
    if (isServerOwner()) return true;
    if (hasPermission('manage_messages')) return true;
    if (hasPermission('pin_messages')) return true;
    return false;
  }

  function canSuperPinMessage(msg) {
    if (!getMessageId(msg)) return false;
    if (isSystemAdmin()) return true;
    if (isServerOwner()) return true;
    if (hasPermission('manage_messages')) return true;
    return false;
  }

  function canEditMessage(msg) {
    if (!getMessageId(msg)) return false;
    if (isSystemAdmin()) return true;
    if (isMyMessage(msg)) return true;
    if (isServerOwner()) return true;
    if (hasPermission('edit_any_message')) return true;
    if (hasPermission('manage_messages')) return true;
    return false;
  }

  function canDeleteMessage(msg) {
    if (!getMessageId(msg)) return false;
    if (isSystemAdmin()) return true;
    // Own messages: Delete own messages (older data without resolved permissions: allowed as before).
    if (isMyMessage(msg) && (!hasPermission('__resolved') || hasPermission('delete_own_messages'))) return true;
    if (isServerOwner()) return true;
    if (hasPermission('delete_any_message')) return true;
    if (hasPermission('manage_messages')) return true;
    return false;
  }

  function canModerateMessage(msg) {
    return canEditMessage(msg) || canDeleteMessage(msg) || canPinMessage(msg) || canSuperPinMessage(msg);
  }

  function isMessageDeleted(msg) {
    const v = msg && msg.deleted_at;
    return v != null && String(v).trim() !== '';
  }

  function isMessageEdited(msg) {
    const v = msg && msg.edited_at;
    return v != null && String(v).trim() !== '';
  }

  function isMessagePinned(msg) {
    const m = safeObj(msg);
    return toInt(m.is_pinned, 0) === 1;
  }

  function isMessageSuperPinned(msg) {
    const m = safeObj(msg);
    if (toInt(m.is_super_pinned, 0) === 1) return true;
    if (toInt(m.is_header_message, 0) === 1) return true;
    return toInt(m.is_motd, 0) === 1;
  }

  function beginEditMessage(rawMsg) {
    const m = safeObj(rawMsg);
    if (!canEditMessage(m)) return;
    if (isMessageDeleted(m)) return;
    const id = getMessageId(m);
    if (!id) return;
    setMessageActionErr('');
    setEditingMessageId(id);
    setEditDraft(String(m.body || ''));
    setHoveredMessageId(id);
  }

  function cancelEditMessage() {
    setEditingMessageId('');
    setEditDraft('');
    setMessageActionErr('');
  }

  async function saveEditMessage(messageId) {
    const id = String(messageId || '').trim();
    if (!id) return;
    const body = String(editDraft || '').trim();
    if (!body) {
      setMessageActionErr('Message cannot be empty');
      return;
    }

    setEditBusyMessageId(id);
    setMessageActionErr('');

    try {
      const res = await apiFetchJson(`/api/coms/messages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ body }),
      });

      const updated = res && res.message ? res.message : null;
      if (updated) {
        applyIncomingUpdateLike(updated);
        if (typeof onMessageMetaChanged === 'function') onMessageMetaChanged(updated);
      }

      setEditingMessageId('');
      setEditDraft('');
    } catch (e) {
      console.error('ChatView edit failed:', e);
      setMessageActionErr((e && e.message) || 'Failed to edit message');
    } finally {
      setEditBusyMessageId('');
    }
  }

  async function deleteMessage(rawMsg) {
    const m = safeObj(rawMsg);
    if (!canDeleteMessage(m)) return;
    if (isMessageDeleted(m)) return;

    const id = getMessageId(m);
    if (!id) return;

    const ok = window.confirm('Delete this message?');
    if (!ok) return;

    setDeleteBusyMessageId(id);
    setMessageActionErr('');

    try {
      await apiFetchJson(`/api/coms/messages/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });

      removeMessageById(id, m.channel_id);
      if (typeof onMessageRemoved === 'function') onMessageRemoved(m);
    } catch (e) {
      console.error('ChatView delete failed:', e);
      setMessageActionErr((e && e.message) || 'Failed to delete message');
    } finally {
      setDeleteBusyMessageId('');
    }
  }

  async function togglePinnedMessage(rawMsg) {
    const m = safeObj(rawMsg);
    if (!canPinMessage(m)) return;
    if (isMessageDeleted(m)) return;

    const id = getMessageId(m);
    if (!id) return;

    setPinBusyMessageId(id);
    setMessageActionErr('');

    try {
      const res = await apiFetchJson(`/api/coms/messages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_pinned: isMessagePinned(m) ? 0 : 1 }),
      });

      const updated = res && res.message ? res.message : null;
      if (updated) {
        applyIncomingUpdateLike(updated);
        if (typeof onMessageMetaChanged === 'function') onMessageMetaChanged(updated);
      }
    } catch (e) {
      console.error('ChatView pin failed:', e);
      setMessageActionErr((e && e.message) || 'Failed to update pin');
    } finally {
      setPinBusyMessageId('');
    }
  }

  async function toggleSuperPinnedMessage(rawMsg) {
    const m = safeObj(rawMsg);
    if (!canSuperPinMessage(m)) return;
    if (isMessageDeleted(m)) return;

    const id = getMessageId(m);
    if (!id) return;

    setSuperPinBusyMessageId(id);
    setMessageActionErr('');

    try {
      const res = await apiFetchJson(`/api/coms/messages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_super_pinned: isMessageSuperPinned(m) ? 0 : 1 }),
      });

      const updated = res && res.message ? res.message : null;
      if (updated) {
        applyIncomingUpdateLike(updated);
        if (typeof onMessageMetaChanged === 'function') onMessageMetaChanged(updated);
      }
    } catch (e) {
      console.error('ChatView super pin failed:', e);
      setMessageActionErr((e && e.message) || 'Failed to update header message');
    } finally {
      setSuperPinBusyMessageId('');
    }
  }

  return {
    canModerateMessage,
    canEditMessage,
    canDeleteMessage,
    canPinMessage,
    canSuperPinMessage,
    isMessageDeleted,
    isMessageEdited,
    isMessagePinned,
    isMessageSuperPinned,
    beginEditMessage,
    cancelEditMessage,
    saveEditMessage,
    deleteMessage,
    togglePinnedMessage,
    toggleSuperPinnedMessage,
  };
}